import { Card, CardBody, cardTitleVariants } from './Card'
import { Page } from './Page'
import type { ThemeVariant } from './theme-tokens'

import { cn } from '@/shared/lib/utils'

interface StatusScreenProps {
  /** Tema propagado para `Page` e `Card` — mantém a tela na linguagem do portal. */
  variant?: ThemeVariant
  /** Ilustração/mascote. Decorativa por padrão; passe `illustrationAlt` quando carregar informação. */
  illustrationSrc?: string
  illustrationAlt?: string
  title: string
  description?: React.ReactNode
  /** Slot livre abaixo da descrição — contador, ações, detalhes. */
  children?: React.ReactNode
  className?: string
}

/**
 * Tela de estado de página inteira: ilustração + título + descrição, sobre a
 * superfície `Page` + `Card` que é o padrão de portal em vigor.
 *
 * Existe porque telas de estado escritas à mão vinham repetindo
 * `bg-background text-foreground` — e neste tema `--background` e
 * `--foreground` são o mesmo `#1d1d2a`, ou seja, texto invisível. Aqui a
 * superfície e o texto vêm dos tokens do `Card`, que já são um par legível.
 *
 * Agnóstica de domínio: não conhece manutenção, 404 nem erro — quem dá o
 * significado é quem compõe.
 */
export function StatusScreen({
  variant,
  illustrationSrc,
  illustrationAlt,
  title,
  description,
  children,
  className
}: StatusScreenProps) {
  return (
    <Page variant={variant}>
      <Card variant={variant} className={className}>
        <CardBody
          className={cn('flex flex-col items-center gap-5 py-8 text-center')}
        >
          {illustrationSrc && (
            <img
              src={illustrationSrc}
              alt={illustrationAlt ?? ''}
              aria-hidden={illustrationAlt ? undefined : true}
              className="h-36 w-36 object-contain sm:h-44 sm:w-44"
            />
          )}

          <h1 className={cardTitleVariants({ variant, className: 'text-2xl' })}>
            {title}
          </h1>

          {description && (
            <p className="text-american-blue max-w-sm">{description}</p>
          )}

          {children}
        </CardBody>
      </Card>
    </Page>
  )
}

StatusScreen.displayName = 'StatusScreen'
